import { Injectable } from '@angular/core';
import { ICompaniesProvider } from '../data-providers/interfaces/companies-provider.interface';
import { LoaderService } from '../loader.service';
import { CompaniesDataProvider } from '../data-providers/companies.data-provider';
import { CompanyService } from '../company.service';
import { Company } from '../../models/company.model';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { Manager, ManagerInfoToCreate } from '../../models/manager.model';
import { ICommonList } from '../data-providers/common.data-provider';
import { CompanyOptionsDataProvider } from '../data-providers/company-options.data-provider';
import { ICompanyOptionsProvider } from '../data-providers/interfaces/company-options-provider.interface';
import { CompanyOption } from '../../models/company-option.model';
import { User } from '../../models/user.model';

function updateCurrentCompanyIfNeeded<T>(
  companyService: CompanyService,
  id: number
): (observable: Observable<T>) => Observable<T> {
  return observable =>
    observable.pipe(
      tap(() => {
        if (id === companyService.COMPANY_ID) {
          companyService.setCompanyId(id);
        }
      })
    );
}

@Injectable()
export class CompaniesRefreshCurrentCompanyDataProvider implements ICompaniesProvider {
  constructor(
    private companyService: CompanyService,
    private companiesProvider: CompaniesDataProvider
  ) {}

  loaderService: LoaderService = this.companiesProvider.loaderService;

  activateCompany(id: number): Observable<any> {
    return this.companiesProvider
      .activateCompany(id)
      .pipe(updateCurrentCompanyIfNeeded(this.companyService, id));
  }

  hideCompany(id: number): Observable<any> {
    return this.companiesProvider
      .hideCompany(id)
      .pipe(updateCurrentCompanyIfNeeded(this.companyService, id));
  }

  updateCompany(id: number, body: Partial<Company>): Observable<Company> {
    return this.companiesProvider
      .updateCompany(id, body)
      .pipe(updateCurrentCompanyIfNeeded(this.companyService, id));
  }

  createCompany(body: Partial<Company>): Observable<Company> {
    return this.companiesProvider.createCompany(body);
  }

  createManager(companyId: number, manager: ManagerInfoToCreate): Observable<Manager> {
    return this.companiesProvider.createManager(companyId, manager);
  }

  findManager(companyId: number, email: string, restaurantId?: number): Observable<User> {
    return this.companiesProvider.findManager(companyId, email, restaurantId);
  }

  dismissInvite(companyId: any, inviteId: any): Observable<any> {
    return this.companiesProvider.dismissInvite(companyId, inviteId);
  }

  dismissManager(companyId: any, managerId: any): Observable<any> {
    return this.companiesProvider.dismissManager(companyId, managerId);
  }

  getCompanies(): Observable<ICommonList<Company>> {
    return this.companiesProvider.getCompanies();
  }

  getCompaniesListSubscription(): Observable<ICommonList<Company>> {
    return this.companyService.getCompaniesList();
  }

  getCompany(id: any): Observable<Company> {
    return this.companiesProvider.getCompany(id);
  }

  getCompanyManagers(id: number): Observable<ICommonList<Manager>> {
    return this.companiesProvider.getCompanyManagers(id);
  }

  inviteManager(companyId: any, email: any): Observable<any> {
    return this.companiesProvider.inviteManager(companyId, email);
  }

  removeCompany(id: number): Observable<any> {
    return this.companiesProvider.removeCompany(id);
  }

  updateManagerPassword(companyId: number, managerId: number, password: string): Observable<void> {
    return this.companiesProvider.updateManagerPassword(companyId, managerId, password);
  }
}

@Injectable()
export class CompanyOptionsRefreshCurrentCompanyDataProvider implements ICompanyOptionsProvider {
  constructor(
    private companyService: CompanyService,
    private companyOptionsProvider: CompanyOptionsDataProvider
  ) {}

  loaderService = this.companyOptionsProvider.loaderService;

  createCompanyOption(companyId: number, data: Partial<CompanyOption>): Observable<CompanyOption> {
    return this.companyOptionsProvider
      .createCompanyOption(companyId, data)
      .pipe(updateCurrentCompanyIfNeeded(this.companyService, companyId));
  }

  deleteCompanyOption(companyId: number, companyOptionId: number): Observable<any> {
    return this.companyOptionsProvider
      .deleteCompanyOption(companyId, companyOptionId)
      .pipe(updateCurrentCompanyIfNeeded(this.companyService, companyId));
  }

  getCompanyOption(companyId: number, companyOptionId: number): Observable<CompanyOption> {
    return this.companyOptionsProvider.getCompanyOption(companyId, companyOptionId);
  }

  getCompanyOptions(companyId: number): Observable<CompanyOption[]> {
    return this.companyOptionsProvider.getCompanyOptions(companyId);
  }

  toggleCompanyOptionActivation(companyId: number, companyOptionId: number): Observable<any> {
    return this.companyOptionsProvider
      .toggleCompanyOptionActivation(companyId, companyOptionId)
      .pipe(updateCurrentCompanyIfNeeded(this.companyService, companyId));
  }

  updateCompanyOption(
    companyId: number,
    companyOptionId: number,
    data: Partial<CompanyOption>
  ): Observable<CompanyOption> {
    return this.companyOptionsProvider
      .updateCompanyOption(companyId, companyOptionId, data)
      .pipe(updateCurrentCompanyIfNeeded(this.companyService, companyId));
  }
}
